import React from "react";
import { useMutation } from "@tanstack/react-query";
import { Form, Input, Button } from "antd";
import {
  MailOutlined,
  ArrowLeftOutlined,
  LockOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import ErrorBlock from "../../components/UI/ErrorBlock.js";
import { ResetPass } from "../../utils/api.js";
import login1 from "../../assets/img/login1.png";

function ResetEmail() {
  const [form] = Form.useForm();

  const { mutate, isError, error, isLoading } = useMutation({
    mutationFn: ResetPass,
    onSuccess: async () => {
      form.resetFields();
    },
    onError: (err) => {
      console.error("Password reset link failed", err);
    },
  });

  const handleSubmit = (values) => {
    mutate(values);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gh-primary-lighter/10 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
      <div className="flex flex-col lg:flex-row w-full max-w-5xl bg-white dark:bg-gray-800 rounded-2xl shadow-gh-lg overflow-hidden">
        {/* Brand Side (matches Signin) */}
        <div className="w-full lg:w-1/2 min-h-[220px] lg:min-h-[600px] relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-gh-primary/90 to-gh-primary-dark/95 z-10" />
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${login1})` }}
          />
          <div className="relative z-20 flex flex-col items-center justify-center h-full text-white p-10">
            <div className="w-16 h-16 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center mb-6">
              <span className="text-3xl font-bold">GH</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold mb-3 tracking-tight">
              GuestHome
            </h2>
            <p className="text-white/70 text-center text-sm lg:text-base max-w-xs">
              Securely reset your password in just a few seconds.
            </p>

            {/* Helpful tips */}
            <ul className="mt-8 space-y-2 text-white/80 text-sm max-w-xs w-full">
              <li className="flex items-start gap-2">
                <CheckCircleFilled className="text-gh-teal mt-0.5" />
                <span>Check your inbox for the reset link.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircleFilled className="text-gh-teal mt-0.5" />
                <span>The link expires in 30 minutes.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircleFilled className="text-gh-teal mt-0.5" />
                <span>Can't find it? Check your spam folder.</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Form Side */}
        <div className="w-full lg:w-1/2 p-8 lg:p-12">
          <Link
            to="/signin"
            className="inline-flex items-center gap-1 text-sm text-gh-primary hover:text-gh-primary-dark font-medium transition-colors mb-6"
          >
            <ArrowLeftOutlined />
            Back to sign in
          </Link>

          <div className="mb-8">
            <h1 className="text-2xl lg:text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">
              Reset password
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mt-2">
              Enter the email associated with your account and we'll send a
              secure link to reset your password.
            </p>
          </div>

          <Form form={form} layout="vertical" onFinish={handleSubmit} size="large">
            <Form.Item
              label="Email address"
              name="email"
              rules={[
                { required: true, message: "Email is required" },
                { type: "email", message: "Please enter a valid email" },
              ]}
            >
              <Input
                prefix={<MailOutlined className="text-gray-400 mr-1" />}
                placeholder="you@example.com"
                className="rounded-xl"
                autoComplete="email"
              />
            </Form.Item>

            <Form.Item className="!mb-2">
              <Button
                type="primary"
                htmlType="submit"
                block
                loading={isLoading}
                icon={<LockOutlined />}
                className="h-12 rounded-xl font-semibold text-base"
                style={{
                  background: "linear-gradient(135deg, #55497C, #6c5ce7)",
                  border: "none",
                }}
              >
                Send Reset Link
              </Button>
            </Form.Item>

            {!isError && (
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-4">
                We'll never share your email with anyone else.
              </p>
            )}
          </Form>

          {isError && (
            <ErrorBlock
              title="Reset failed"
              message={
                error?.response?.data?.message ||
                error?.response?.data?.detail ||
                "We couldn't find an account with that email."
              }
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default ResetEmail;
